import { apiFetch } from './client';

/**
 * Events service — the ONLY place that should know whether event data
 * comes from Google Calendar or somewhere else. Components, hooks, and
 * AppContext never change; they only ever call the functions below.
 *
 * All four operations now call the Cloudflare Pages Functions under
 * /api/events (see /functions/api/events.js for GET/POST and
 * /functions/api/events/[id].js for PATCH/DELETE), which proxy to the
 * Google Calendar API using OAuth credentials stored in Cloudflare
 * Secrets. See those files for the field mapping between a Google
 * Calendar Event resource and this app's
 * { id, title, date, allDay, start, end, location, icon, color }
 * shape.
 */

// Local cache of the last GET response, used only by getEventById()
// for a synchronous-feeling lookup (e.g. opening the detail sheet)
// without an extra round trip.
let localCache = [];

export async function getEvents(range = {}) {
  const params = new URLSearchParams();
  if (range.timeMin) params.set('timeMin', range.timeMin);
  if (range.timeMax) params.set('timeMax', range.timeMax);
  const query = params.toString();

  const events = await apiFetch(`/api/events${query ? `?${query}` : ''}`);
  localCache = events;
  return events;
}

export async function getEventById(id) {
  return localCache.find((e) => e.id === id) ?? null;
}

export async function createEvent(eventData) {
  const created = await apiFetch('/api/events', {
    method: 'POST',
    body: JSON.stringify(eventData),
  });
  localCache = [...localCache, created];
  return created;
}

export async function updateEvent(id, updates) {
  const updated = await apiFetch(`/api/events/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
  localCache = localCache.map((e) => (e.id === id ? updated : e));
  return updated;
}

export async function deleteEvent(id) {
  await apiFetch(`/api/events/${encodeURIComponent(id)}`, { method: 'DELETE' });
  localCache = localCache.filter((e) => e.id !== id);
  return true;
}
